import { useState } from "react";
import {
  getVersions,
  getFeatureRequests,
  getBugReports,
  promoteVersion,
  rollbackVersion,
  linkFeatureToVersion,
  linkBugToVersion,
} from "../data/store";
import type { Version, Stage } from "../types";

const stageOrder: Stage[] = ["dev", "beta", "prod"];
const stageLabels: Record<Stage, string> = { dev: "Dev", beta: "Beta", prod: "Prod" };
const stageColors: Record<Stage, string> = { dev: "#3b82f6", beta: "#f59e0b", prod: "#22c55e" };

export default function Versions() {
  const [versions, setVersions] = useState(getVersions());
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);

  const features = getFeatureRequests();
  const bugs = getBugReports();

  function handlePromote(version: Version) {
    const currentIdx = stageOrder.indexOf(version.stage);
    if (currentIdx >= stageOrder.length - 1) return;
    const nextStage = stageOrder[currentIdx + 1];
    promoteVersion(version.id, nextStage);
    setVersions(getVersions());
  }

  function handleRollback(version: Version) {
    rollbackVersion(version.id);
    setVersions(getVersions());
  }

  function handleLinkFeature(versionId: string, featureId: string) {
    linkFeatureToVersion(versionId, featureId);
    setVersions(getVersions());
  }

  function handleLinkBug(versionId: string, bugId: string) {
    linkBugToVersion(versionId, bugId);
    setVersions(getVersions());
  }

  const linkedFeatures = selectedVersion
    ? features.filter((f) => selectedVersion.linkedFeatures.includes(f.id))
    : [];
  const linkedBugs = selectedVersion
    ? bugs.filter((b) => selectedVersion.linkedBugs.includes(b.id))
    : [];
  const unlinkableFeatures = features.filter(
    (f) => !f.completedInVersion && f.status !== "completed"
  );
  const unlinkableBugs = bugs.filter((b) => !b.fixedInVersion && b.status !== "completed");

  return (
    <div className="page">
      <div className="page-header">
        <h2>Version Controller</h2>
        <p>Manage POS System deployments, promote versions across environments, and track what shipped.</p>
      </div>

      {/* Version cards by stage */}
      <div className="version-stages">
        {stageOrder.map((stage) => {
          const stageVersions = versions.filter((v) => v.stage === stage && v.status === "deployed");
          const latest = stageVersions[0];
          return (
            <div key={stage} className="version-stage-card">
              <div className="vsc-header" style={{ borderColor: stageColors[stage] }}>
                <span className="vsc-stage-name">{stageLabels[stage]}</span>
                <span className="vsc-version">{latest ? `v${latest.version}` : "—"}</span>
              </div>
              {latest && (
                <div className="vsc-details">
                  <code>{latest.commitHash}</code>
                  <span className="vsc-time">{new Date(latest.timestamp).toLocaleDateString()}</span>
                  <div className="vsc-actions">
                    {stage !== "prod" && (
                      <button className="promote-btn" onClick={() => handlePromote(latest)}>
                        Promote → {stageLabels[stageOrder[stageOrder.indexOf(stage) + 1]]}
                      </button>
                    )}
                    <button className="rollback-btn" onClick={() => handleRollback(latest)}>
                      Rollback
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Full version history */}
      <div className="card" style={{ marginTop: "2rem" }}>
        <h3 style={{ marginBottom: "1rem" }}>Version History</h3>
        <div className="version-list">
          {versions.map((v) => (
            <div
              key={v.id}
              className={`version-item ${selectedVersion?.id === v.id ? "selected" : ""} ${v.status === "rolled-back" ? "rolled-back" : ""}`}
              onClick={() => setSelectedVersion(v)}
            >
              <div className="vi-left">
                <div className="vi-indicator" style={{ backgroundColor: stageColors[v.stage] }} />
                <div className="vi-info">
                  <div className="vi-title">
                    <strong>v{v.version}</strong>
                    <span className={`badge badge-${v.stage === "prod" ? "success" : v.stage === "beta" ? "warning" : "info"}`}>
                      {stageLabels[v.stage]}
                    </span>
                    {v.status === "rolled-back" && <span className="badge badge-danger">rolled back</span>}
                  </div>
                  <div className="vi-commit">
                    <code>{v.commitHash}</code> {v.commitMessage}
                  </div>
                </div>
              </div>
              <div className="vi-right">
                <div className="vi-stats">
                  {v.linkedFeatures.length > 0 && <span title="Features">✨ {v.linkedFeatures.length}</span>}
                  {v.linkedBugs.length > 0 && <span title="Bug fixes">🐛 {v.linkedBugs.length}</span>}
                </div>
                <div className="vi-time">{new Date(v.timestamp).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Version detail panel */}
      {selectedVersion && (
        <div className="card" style={{ marginTop: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3>
              v{selectedVersion.version}
              <span
                className={`badge badge-${selectedVersion.stage === "prod" ? "success" : selectedVersion.stage === "beta" ? "warning" : "info"}`}
                style={{ marginLeft: "0.75rem" }}
              >
                {stageLabels[selectedVersion.stage]}
              </span>
            </h3>
            <button className="btn btn-primary" onClick={() => setShowLinkModal(!showLinkModal)}>
              + Link Items
            </button>
          </div>

          <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div>
              <h4 style={{ marginBottom: "0.5rem", fontSize: "0.9rem", color: "var(--text-muted)" }}>
                ✨ Features ({linkedFeatures.length})
              </h4>
              {linkedFeatures.length === 0 ? (
                <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>No features linked.</p>
              ) : (
                linkedFeatures.map((f) => (
                  <div key={f.id} className="linked-item">
                    <span className="badge badge-success">✓</span>
                    <span style={{ marginLeft: "0.5rem" }}>{f.title}</span>
                  </div>
                ))
              )}
            </div>
            <div>
              <h4 style={{ marginBottom: "0.5rem", fontSize: "0.9rem", color: "var(--text-muted)" }}>
                🐛 Bugs Fixed ({linkedBugs.length})
              </h4>
              {linkedBugs.length === 0 ? (
                <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>No bugs linked.</p>
              ) : (
                linkedBugs.map((b) => (
                  <div key={b.id} className="linked-item">
                    <span className="badge badge-success">✓</span>
                    <span style={{ marginLeft: "0.5rem" }}>{b.title}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {showLinkModal && (
            <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--bg)", borderRadius: "8px" }}>
              <h4 style={{ marginBottom: "0.75rem" }}>Link to v{selectedVersion.version}</h4>
              {unlinkableFeatures.length > 0 && (
                <div style={{ marginBottom: "1rem" }}>
                  <h5 style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Features</h5>
                  {unlinkableFeatures.map((f) => (
                    <div key={f.id} style={{ display: "flex", alignItems: "center", marginBottom: "0.25rem" }}>
                      <button className="btn btn-success" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }} onClick={() => handleLinkFeature(selectedVersion.id, f.id)}>
                        Link
                      </button>
                      <span style={{ marginLeft: "0.5rem", fontSize: "0.85rem" }}>{f.title}</span>
                    </div>
                  ))}
                </div>
              )}
              {unlinkableBugs.length > 0 && (
                <div>
                  <h5 style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Bug Reports</h5>
                  {unlinkableBugs.map((b) => (
                    <div key={b.id} style={{ display: "flex", alignItems: "center", marginBottom: "0.25rem" }}>
                      <button className="btn btn-success" style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }} onClick={() => handleLinkBug(selectedVersion.id, b.id)}>
                        Link
                      </button>
                      <span style={{ marginLeft: "0.5rem", fontSize: "0.85rem" }}>{b.title}</span>
                    </div>
                  ))}
                </div>
              )}
              {unlinkableFeatures.length === 0 && unlinkableBugs.length === 0 && (
                <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>All items already linked.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
